import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Notifications as NotificationsIcon,
  PeopleAlt as PeopleAltIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import LoginHistoryDialog from './LoginHistoryDialog';
import NotificationSettings from './NotificationSettings';

const AdminManagementPage = () => {
  const { currentUser, idToken } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyEmail, setHistoryEmail] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    role: 'user',
    isActive: true,
    notes: ''
  });

  useEffect(() => {
    if (idToken) {
      fetchUsers();
    }
  }, [idToken]);

  const fetchUsers = async () => {
    if (!idToken) {
      console.log('No idToken available, skipping fetch');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiService.baseURL}/api/allowed-users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      } else {
        toast.error('사용자 목록을 불러오는데 실패했습니다');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('사용자 목록 조회 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditMode(true);
      setSelectedUser(user);
      setFormData({
        email: user.email,
        displayName: user.display_name || '',
        role: user.role,
        isActive: user.is_active,
        notes: user.notes || ''
      });
    } else {
      setEditMode(false);
      setSelectedUser(null);
      setFormData({
        email: '',
        displayName: '',
        role: 'admin',
        isActive: true,
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setFormData({
      email: '',
      displayName: '',
      role: 'admin',
      isActive: true,
      notes: ''
    });
  };

  const handleSubmit = async () => {
    if (!idToken) {
      toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      return;
    }

    try {
      if (editMode && selectedUser) {
        const response = await fetch(`${apiService.baseURL}/api/allowed-users/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            displayName: formData.displayName,
            role: formData.role,
            isActive: formData.isActive,
            notes: formData.notes
          })
        });

        if (response.ok) {
          toast.success('사용자 정보가 수정되었습니다');
          fetchUsers();
          handleCloseDialog();
        } else {
          const error = await response.json();
          toast.error(error.message || '사용자 정보 수정에 실패했습니다');
        }
      } else {
        const response = await fetch(`${apiService.baseURL}/api/allowed-users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: formData.email,
            displayName: formData.displayName,
            role: formData.role,
            notes: formData.notes
          })
        });

        if (response.ok) {
          toast.success('사용자가 추가되었습니다');
          fetchUsers();
          handleCloseDialog();
        } else {
          const error = await response.json();
          toast.error(error.message || '사용자 추가에 실패했습니다');
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('저장 중 오류가 발생했습니다');
    }
  };

  const handleDelete = async (userId, userEmail) => {
    if (!window.confirm(`${userEmail} 사용자를 삭제하시겠습니까?`)) {
      return;
    }

    if (!idToken) {
      toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      return;
    }

    try {
      const response = await fetch(`${apiService.baseURL}/api/allowed-users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('사용자가 삭제되었습니다');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.message || '사용자 삭제에 실패했습니다');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('삭제 중 오류가 발생했습니다');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'error';
      case 'admin':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'superadmin':
        return '슈퍼관리자';
      case 'admin':
        return '관리자';
      default:
        return role;
    }
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} aria-label="admin tabs">
          <Tab icon={<PeopleAltIcon fontSize="small" />} iconPosition="start" value="users" label="사용자 관리" />
          <Tab icon={<NotificationsIcon fontSize="small" />} iconPosition="start" value="notifications" label="알림 설정" />
        </Tabs>
      </Box>

      {activeTab === 'notifications' && <NotificationSettings />}

      {activeTab === 'users' && <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            사용자 관리
          </Typography>
          <Typography variant="body2" color="text.secondary">
            로그인 가능한 사용자를 관리합니다 (PostgreSQL 기반)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#FF4444', '&:hover': { bgcolor: '#CC0000' } }}
        >
          사용자 추가
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>이메일</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>역할</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>상태</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>마지막 로그인</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>로그인 횟수</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.display_name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getRoleLabel(user.role)}
                      color={getRoleBadgeColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? '활성' : '비활성'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.last_login_at
                      ? new Date(user.last_login_at).toLocaleString('ko-KR')
                      : '-'}
                  </TableCell>
                  <TableCell>{user.login_count || 0}회</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => {
                        setHistoryEmail(user.email);
                        setHistoryDialogOpen(true);
                      }}
                      size="small"
                      color="info"
                      title="로그인 이력"
                    >
                      <HistoryIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDialog(user)}
                      size="small"
                      color="primary"
                      title="수정"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(user.id, user.email)}
                      size="small"
                      color="error"
                      disabled={user.email === currentUser?.email}
                      title="삭제"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {users.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">등록된 사용자가 없습니다</Typography>
          </Box>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? '사용자 수정' : '사용자 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="이메일"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
              disabled={editMode}
              helperText={editMode ? '이메일은 수정할 수 없습니다' : 'Google 계정 이메일을 입력하세요'}
            />

            <TextField
              label="이름"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>역할</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="역할"
              >
                <MenuItem value="superadmin">슈퍼관리자 (모든 권한)</MenuItem>
                <MenuItem value="admin">관리자 (전체 권한)</MenuItem>
              </Select>
            </FormControl>

            {editMode && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="활성화"
              />
            )}

            <TextField
              label="메모"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder="관리자용 메모 (선택사항)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!editMode && !formData.email}
            sx={{ bgcolor: '#FF4444', '&:hover': { bgcolor: '#CC0000' } }}
          >
            {editMode ? '수정' : '추가'}
          </Button>
        </DialogActions>
      </Dialog>

      <LoginHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        userEmail={historyEmail}
      />
      </Box>}
    </Box>
  );
};

export default AdminManagementPage;
